/**
 * Viewer Camera View — quick Save/Restore for the 3D viewer camera.
 *
 * Per-folder (i.e. per model-group) persistence in localStorage. Auto-restores
 * on viewer open unless ?share=<id> already provides explicit state.
 *
 * Storage key: `echov:viewer:cameraView:<userId>:<folderKey>`
 * Payload:
 *   {
 *     position: [x, y, z],
 *     target:   [x, y, z],
 *     savedAt:  ISO-8601 string,
 *   }
 *
 * The folder key prefers `folderNameParam`. When that's missing we fall back to
 * a stable hash of the loaded modelIds so different model groups still get
 * their own remembered view.
 */

import * as THREE from "three";

export interface CameraView {
  position: [number, number, number];
  target: [number, number, number];
  savedAt: string;
}

interface MinimalWorld {
  camera: {
    controls: {
      getPosition: (out: THREE.Vector3) => void;
      getTarget: (out: THREE.Vector3) => void;
      setLookAt: (
        px: number, py: number, pz: number,
        tx: number, ty: number, tz: number,
        enableTransition?: boolean,
      ) => Promise<unknown> | unknown;
    };
    three: THREE.Camera;
  };
}

/** Derive a stable storage key suffix for a model group. */
export function makeFolderKey(
  folderName: string | null | undefined,
  modelIds: ReadonlyArray<string>,
): string {
  if (folderName && folderName.trim().length > 0) return folderName.trim();
  if (modelIds.length === 0) return "__nofolder__";
  const sorted = [...modelIds].map((s) => String(s)).sort();
  // tiny djb2 hash so we don't blow up the key length
  let hash = 5381;
  const joined = sorted.join("|");
  for (let i = 0; i < joined.length; i++) {
    hash = ((hash << 5) + hash + joined.charCodeAt(i)) | 0;
  }
  return `group-${(hash >>> 0).toString(36)}`;
}

function getCurrentUserId(): string {
  try {
    const raw =
      sessionStorage.getItem("user") || localStorage.getItem("user") || "";
    if (!raw) return "anon";
    const u = JSON.parse(raw);
    return String(u?.id || u?._id || u?.email || u?.username || "anon");
  } catch {
    return "anon";
  }
}

function storageKey(folderKey: string): string {
  return `echov:viewer:cameraView:${getCurrentUserId()}:${folderKey}`;
}

/** Read the current camera (position + look-at target) in world coordinates. */
export function captureCameraView(world: MinimalWorld): CameraView {
  const pos = new THREE.Vector3();
  const tgt = new THREE.Vector3();
  try {
    world.camera.controls.getPosition(pos);
  } catch {
    pos.copy((world.camera.three as THREE.Camera).position);
  }
  try {
    world.camera.controls.getTarget(tgt);
  } catch {
    // best-effort fallback: look 1 unit forward
    const dir = new THREE.Vector3();
    (world.camera.three as THREE.Camera).getWorldDirection(dir);
    tgt.copy(pos).add(dir);
  }
  return {
    position: [pos.x, pos.y, pos.z],
    target: [tgt.x, tgt.y, tgt.z],
    savedAt: new Date().toISOString(),
  };
}

/** Apply a saved view to the camera. Returns true on success. */
export function applyCameraView(
  world: MinimalWorld,
  view: CameraView | null | undefined,
  options?: { animate?: boolean },
): boolean {
  if (!view || !Array.isArray(view.position) || !Array.isArray(view.target)) {
    return false;
  }
  const [px, py, pz] = view.position;
  const [tx, ty, tz] = view.target;
  if ([px, py, pz, tx, ty, tz].some((n) => typeof n !== "number" || !isFinite(n))) {
    return false;
  }
  try {
    world.camera.controls.setLookAt(px, py, pz, tx, ty, tz, !!options?.animate);
    return true;
  } catch (err) {
    console.warn("[viewerCameraView] setLookAt failed", err);
    return false;
  }
}

/** Persist a view to localStorage. */
export function saveCameraView(folderKey: string, view: CameraView): boolean {
  try {
    localStorage.setItem(storageKey(folderKey), JSON.stringify(view));
    return true;
  } catch (err) {
    console.warn("[viewerCameraView] save failed", err);
    return false;
  }
}

/** Load a previously saved view from localStorage. */
export function loadCameraView(folderKey: string): CameraView | null {
  try {
    const raw = localStorage.getItem(storageKey(folderKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !Array.isArray(parsed.position) ||
      !Array.isArray(parsed.target)
    ) {
      return null;
    }
    return parsed as CameraView;
  } catch (err) {
    console.warn("[viewerCameraView] load failed", err);
    return null;
  }
}

/** Remove a saved view. */
export function clearCameraView(folderKey: string): boolean {
  try {
    localStorage.removeItem(storageKey(folderKey));
    return true;
  } catch {
    return false;
  }
}

/**
 * Minimal toast helper for save/restore confirmations. Self-contained so the
 * viewer doesn't need a UI dependency just for this.
 */
export function showCameraToast(
  message: string,
  variant: "success" | "info" | "warn" = "success",
): void {
  const colorByVariant: Record<string, string> = {
    success: "#44d3de",
    info: "#7ce8f1",
    warn: "#ffb86b",
  };
  const accent = colorByVariant[variant] || colorByVariant.success;

  const el = document.createElement("div");
  el.textContent = message;
  el.style.cssText = `
    position: fixed;
    bottom: 84px;
    left: 50%;
    transform: translateX(-50%) translateY(8px);
    z-index: 99999;
    padding: 0.55rem 0.95rem;
    background: rgba(10, 14, 39, 0.95);
    color: ${accent};
    border: 1px solid ${accent};
    border-radius: 8px;
    box-shadow: 0 0 18px rgba(68, 211, 222, 0.35);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    opacity: 0;
    transition: opacity 160ms ease-out, transform 220ms ease-out;
    pointer-events: none;
  `;
  document.body.appendChild(el);
  requestAnimationFrame(() => {
    el.style.opacity = "1";
    el.style.transform = "translateX(-50%) translateY(0)";
  });
  window.setTimeout(() => {
    el.style.opacity = "0";
    el.style.transform = "translateX(-50%) translateY(-4px)";
    window.setTimeout(() => {
      el.remove();
    }, 240);
  }, 1600);
}
